/*
 * frame-addon-cart.js - Track E, E1 add-on v2, extended v3 (2026-08-18),
 * v4 (2026-08-19).
 *
 * Keeps a framed order's cart lines consistent after the initial add:
 * print/frame/service quantities stay linked, and removing a line cascades
 * down the hierarchy - remove the print, frame + service go too; remove the
 * frame, service goes too; remove the service, nothing else changes (v3:
 * the service line's own remove control is also hidden in the cart
 * templates themselves - see patches/theme/frame-addon/sections/main-cart.liquid
 * and snippets/mini-cart.liquid - so this cascade rule is now a backstop,
 * not the only thing stopping a bare service line).
 * assets/frame-addon.js is what CREATES the three lines (each carrying a
 * shared `_wj_set` token and a `_wj_role` of 'print'/'frame'/'service' as
 * hidden, underscore-prefixed properties); this file only ever reads those
 * back, on the cart page and in the mini-cart drawer.
 *
 * Reconcile-after-the-fact, not click interception. This theme's own
 * remove/quantity actions (assets/theme.js's `_updateQuantity`) target
 * cart lines by POSITIONAL INDEX (`data-line="{{ forloop.index }}"`), which
 * shifts after every removal, and the only event it dispatches afterward -
 * `cart:rerendered` - carries no detail about what changed. So: let the
 * theme's own action land, then fetch the resulting cart and correct
 * anything now inconsistent, targeting each line by its stable `key`
 * (Shopify's own unique per-line id, unaffected by other lines shifting)
 * rather than by position.
 *
 * v3 adds two things on top of the reconcile loop itself:
 *   - A direct fix for the header cart-count bubble, which otherwise can
 *     read a stale number for the length of one dispatch chain (theme.js's
 *     own _updateQuantity sets it from the single-line change response,
 *     before this file's corrections have applied - see reconcile()).
 *   - A checkout guard: the "checkout" button is a real form submit
 *     (name="checkout" on both sections/main-cart.liquid and
 *     snippets/mini-cart.liquid), intercepted so a customer clicking
 *     checkout right after a removal can't reach it before the cascade
 *     correction has actually landed - the failure mode being a real order
 *     with a frame line and no print to put in it.
 *
 * v4: a cascade DELETE (removing a print takes its frame/service with it -
 * a `quantity: 0` correction, as distinct from a lockstep quantity sync)
 * now reloads the page instead of patching the DOM, once the correction has
 * landed server-side. The multi-fetch reconcile dance (apply corrections,
 * re-fetch, patch the header count, ask the theme to re-render) is exactly
 * what's visible/janky about a cascade delete; a reload shows the server's
 * own truth with nothing left to keep in sync by hand. Scoped narrowly:
 * only a delete reloads (a quantity-only lockstep sync still patches in
 * place, unchanged), and the checkout guard's own pass never reloads
 * (see onCheckoutClick) - clicking checkout should still reach checkout,
 * not get stranded by a reload it didn't ask for.
 *
 * ES5, matches frame-addon.js's target range. Loaded sitewide from
 * layout/theme.liquid, not from frame-addon.liquid - the mini-cart drawer
 * (and the checkout button) is reachable from every page, not just product
 * pages.
 */
(function () {
  'use strict';

  if (window.__walljarFrameAddonCart) { return; }
  window.__walljarFrameAddonCart = true;

  /* Pure - takes the plain `items` array from a /cart.js response (each
   * `{key, quantity, properties}`) and returns the {key, quantity}
   * corrections needed, without touching the network or the DOM. Lines
   * with no `_wj_set` (including anything a customer might add to their
   * cart some other way) are left alone entirely. */
  function computeCartCorrections(items) {
    var groups = {};
    var order = [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var props = item.properties || {};
      var setId = props._wj_set;
      if (!setId) { continue; }
      if (!groups[setId]) { groups[setId] = []; order.push(setId); }
      groups[setId].push(item);
    }

    var corrections = [];
    for (var g = 0; g < order.length; g++) {
      var members = groups[order[g]];
      var print = null, frame = null, service = null;
      for (var m = 0; m < members.length; m++) {
        var role = (members[m].properties || {})._wj_role;
        if (role === 'print') { print = members[m]; }
        else if (role === 'frame') { frame = members[m]; }
        else if (role === 'service') { service = members[m]; }
      }

      if (!print) {
        /* The print is gone - nothing else in this group should survive it. */
        if (frame) { corrections.push({ key: frame.key, quantity: 0 }); }
        if (service) { corrections.push({ key: service.key, quantity: 0 }); }
        continue;
      }

      if (!frame) {
        /* The frame is gone - a framing-service line means nothing without it. */
        if (service) { corrections.push({ key: service.key, quantity: 0 }); }
        continue;
      }

      /* Print and frame both present - keep frame/service quantity in
       * lockstep with print, whichever line's own stepper was actually used. */
      if (frame.quantity !== print.quantity) {
        corrections.push({ key: frame.key, quantity: print.quantity });
      }
      if (service && service.quantity !== print.quantity) {
        corrections.push({ key: service.key, quantity: print.quantity });
      }
    }
    return corrections;
  }

  function fetchCart() {
    return fetch(window.routes.cartUrl + '.js', {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (response) { return response.json(); });
  }

  function applyCorrection(correction) {
    return fetch(window.routes.cartChangeUrl + '.js', {
      body: JSON.stringify({ id: correction.key, quantity: correction.quantity }),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
  }

  /* Direct fix for the header cart-count bubble (v3). theme.js's own
   * _updateQuantity sets it from the response of the single-line change
   * that triggered this pass - which, for a cascade removal, still counts
   * the not-yet-corrected frame/service lines. cart:refresh below asks the
   * theme to re-render and that SHOULD also fix the bubble via its own
   * _rerender, but this sets it directly from a fresh post-correction
   * cart rather than relying on that dispatch chain alone. */
  function updateHeaderCount(cart) {
    var badges = document.querySelectorAll('.header__cart-count');
    for (var i = 0; i < badges.length; i++) { badges[i].textContent = cart.item_count; }
  }

  /* Applies whatever corrections computeCartCorrections finds, in
   * parallel - each targets a distinct line `key`, so unlike the original
   * add-to-cart sequence these have no ordering dependency on each other.
   * Resolves once the cart is consistent and the UI has been told to
   * refresh; used both by the cart:rerendered listener below and by the
   * checkout guard, which needs to know corrections have actually landed
   * before letting a checkout submit through.
   *
   * `reload`: when true AND at least one applied correction was a removal
   * (quantity: 0 - the !print/!frame cascade branches in
   * computeCartCorrections, as distinct from a quantity: print.quantity
   * lockstep sync), reload the page instead of patching the DOM. A cascade
   * delete is exactly the case the multi-fetch reconcile dance is
   * visible/janky for; a full reload guarantees what's shown matches the
   * server with no separate header-count/re-render patch to keep in sync.
   * A lockstep-only pass (bumping a framed print's own quantity - nothing
   * actually removed) keeps today's DOM-patch behaviour even when `reload`
   * is true, and the checkout guard always passes `reload: false` so a
   * cleanup right before checkout still proceeds to checkout rather than
   * surprising the customer with a reload. */
  function runReconcile(reload) {
    return fetchCart().then(function (cart) {
      var corrections = computeCartCorrections(cart.items || []);
      if (!corrections.length) { return; }

      var isDelete = corrections.some(function (c) { return c.quantity === 0; });

      return Promise.all(corrections.map(applyCorrection)).then(function () {
        if (reload && isDelete) {
          window.location.reload();
          return;
        }
        return fetchCart();
      }).then(function (freshCart) {
        if (!freshCart) { return; }
        updateHeaderCount(freshCart);
        /* The theme's own onpageshow handler (layout/theme.liquid) listens
         * for exactly this event to refresh the cart display - reusing it
         * rather than reimplementing a re-render. */
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { scrollToTop: false }
        }));
      });
    });
  }

  /* Re-entrancy guard: applying a correction itself triggers the theme's
   * own cart:rerendered again, and the checkout guard can call in while a
   * cart:rerendered-triggered pass is already running. Both share the same
   * in-flight promise instead of racing separate fetches - a corrective
   * pass that finds nothing to fix is a no-op, so this settles after at
   * most one extra pass either way.
   *
   * `reload` is OR'd into whichever pass is already in flight: if a
   * cart:rerendered-triggered pass (reload: true) is running when the
   * checkout guard calls in (reload: false), let it finish as a reload -
   * the page is navigating away regardless, so the checkout guard's own
   * proceed() simply never runs, which is fine. */
  var reconciling = null;
  var reconcilingReload = false;

  function reconcile(reload) {
    if (reload) { reconcilingReload = true; }
    if (reconciling) { return reconciling; }
    var wantReload = reconcilingReload;
    reconciling = runReconcile(wantReload).then(function () {
      reconciling = null; reconcilingReload = false;
    }, function () {
      reconciling = null; reconcilingReload = false;
    });
    return reconciling;
  }

  /* Capture phase: the mini-cart drawer dispatches cart:rerendered WITHOUT
   * bubbles: true (assets/theme.js), so a bubble-phase listener on document
   * would miss it. Capture-phase listeners on an ancestor still fire on the
   * way down to the target regardless of the bubbles flag - same reasoning
   * frame-addon.js already uses for its own document-level interception.
   * reload: true - this fires for every real cart change (cart page and
   * drawer alike), which is where a cascade delete needs to reload. */
  document.addEventListener('cart:rerendered', function () { reconcile(true); }, true);

  /* Also reconcile once on load, in case a cart went inconsistent in a
   * session before this shipped - skipped when the cart is empty, the
   * common case, to avoid a network request nearly every visitor doesn't
   * need. window.theme.cartCount is set in layout/theme.liquid.
   * reload: false - there is no user action to react to on a fresh page
   * load, so a stale trio here gets today's silent DOM patch, not a
   * reload-on-arrival surprise. */
  function reconcileIfNonEmpty() {
    if (window.theme && window.theme.cartCount > 0) { reconcile(false); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileIfNonEmpty);
  } else {
    reconcileIfNonEmpty();
  }

  /* --------------------------------------------------------- checkout guard */

  /* Both sections/main-cart.liquid (.cart-recap__checkout) and
   * snippets/mini-cart.liquid render <button type="submit" name="checkout">
   * inside a form posting to routes.cart_url - a real submit, not a link.
   * Clicking it fast right after a removal could otherwise reach checkout
   * before this file's cascade correction has landed server-side, which
   * for a framed order means a real order with a frame line and no print.
   * So: always intercept, run (or wait out) a reconcile pass, then replay
   * the click with a one-shot bypass flag so the second click's native
   * form submission proceeds untouched - same shape as frame-addon.js's
   * own real-add-then-fallback retry in onAddToCartCapture.
   *
   * Always calls reconcile(false) below: even a cascade delete caught here
   * should still proceed to checkout once cleanup lands, not reload and
   * strand the customer mid-checkout-click - the pre-existing "silently fix
   * then proceed" contract this guard already had, unchanged. */
  function onCheckoutClick(event) {
    var target = event.target;
    var button = target && target.closest &&
      target.closest('button[name="checkout"], input[name="checkout"]');
    if (!button) { return; }

    if (button.getAttribute('data-wj-checkout-bypass')) {
      button.removeAttribute('data-wj-checkout-bypass');
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    function proceed() {
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      button.setAttribute('data-wj-checkout-bypass', '1');
      button.click();
    }

    reconcile(false).then(proceed, proceed);
  }

  document.addEventListener('click', onCheckoutClick, true);
}());
